import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
    ArrowLeftIcon,
    MapPinIcon,
    CalendarIcon,
    TicketIcon,
    CurrencyDollarIcon,
    UserCircleIcon,
    PhoneIcon,
    EnvelopeIcon,
    XCircleIcon,
    ArrowPathIcon,
    TruckIcon,
    StarIcon,
    ClockIcon,
} from "@heroicons/react/24/outline";

import { Button } from "@/components/ui/Button";
import { SECTION_PERMISSIONS } from "@/config/permissions";
import { usePermissions } from "@/features/auth/usePermissions";
import { getBookingDetails, cancelBooking, refundBooking } from "../api/rideBookingsTripsApi";
import { extractApiError } from "@/lib/api-error";

// ============================================================================
// Badges
// ============================================================================

const StatusBadge = ({ status }: { status: string }) => {
    const s = status ?? "UNKNOWN";
    const styles: Record<string, string> = {
        PENDING_PAYMENT: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
        CONFIRMED: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
        IN_PROGRESS: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
        COMPLETED: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
        CANCELLED: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
        EXPIRED_NO_SHOW: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
        PAID: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
        PENDING: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
        REFUNDED: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
        FAILED: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
    };
    return (
        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${styles[s] || "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"}`}>
            {s.replace(/_/g, " ")}
        </span>
    );
};

// ============================================================================
// Helpers
// ============================================================================

const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "—";
    try {
        return new Intl.DateTimeFormat("en-NG", {
            month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit",
        }).format(new Date(dateStr));
    } catch {
        return dateStr;
    }
};

const formatCurrency = (amount: number | null | undefined, currency = "NGN") => {
    if (amount == null) return "—";
    return new Intl.NumberFormat("en-NG", { style: "currency", currency, maximumFractionDigits: 0 }).format(amount);
};

// ============================================================================
// Component
// ============================================================================

export const BookingDetailsRoute = () => {
    const { bookingId } = useParams<{ bookingId: string }>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { hasPermission } = usePermissions();
    const [cancelModal, setCancelModal] = useState(false);
    const [refundModal, setRefundModal] = useState(false);
    const [actionReason, setActionReason] = useState("");
    const [refundAmount, setRefundAmount] = useState("");

    const { data, isLoading, error } = useQuery({
        queryKey: ["bookingDetails", bookingId],
        queryFn: () => getBookingDetails(bookingId!),
        enabled: !!bookingId,
    });

    const cancelMutation = useMutation({
        mutationFn: (reason: string) => cancelBooking(bookingId!, reason),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["bookingDetails", bookingId] });
            queryClient.invalidateQueries({ queryKey: ["bookings"] });
            setCancelModal(false);
            setActionReason("");
        },
    });

    const refundMutation = useMutation({
        mutationFn: ({ reason, amount }: { reason: string; amount?: number }) =>
            refundBooking(bookingId!, reason, amount),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["bookingDetails", bookingId] });
            queryClient.invalidateQueries({ queryKey: ["bookings"] });
            setRefundModal(false);
            setActionReason("");
            setRefundAmount("");
        },
    });

    const booking = data?.data;

    if (isLoading) {
        return (
            <div className="flex min-h-[60vh] items-center justify-center">
                <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
            </div>
        );
    }

    if (error || !booking) {
        return (
            <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4">
                <XCircleIcon className="h-12 w-12 text-rose-400" />
                <p className="text-sm text-slate-600 dark:text-slate-400">{error ? extractApiError(error) : "Booking not found"}</p>
                <Button variant="outline" onClick={() => navigate("/ride-bookings-trips/bookings")}>
                    <ArrowLeftIcon className="mr-2 h-4 w-4" />Back to Bookings
                </Button>
            </div>
        );
    }

    const bookingStatus = booking.bookingStatus || booking.status || "UNKNOWN";
    const totalAmount = booking.totalPrice ?? booking.totalAmount ?? 0;
    const canCancel = !["CANCELLED", "COMPLETED", "EXPIRED_NO_SHOW"].includes(bookingStatus);
    const canRefund = bookingStatus === "CANCELLED" && booking.paymentStatus === "PAID";
    const canManageBookings = hasPermission(SECTION_PERMISSIONS.RIDE_BOOKINGS_MANAGE[0]);
    const canRefundBookings = hasPermission(SECTION_PERMISSIONS.RIDE_BOOKINGS_REFUND[0]);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate("/ride-bookings-trips/bookings")}>
                        <ArrowLeftIcon className="h-5 w-5" />
                    </Button>
                    <div>
                        <div className="flex items-center gap-3">
                            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Booking Details</h1>
                            <StatusBadge status={bookingStatus} />
                            <StatusBadge status={booking.paymentStatus} />
                        </div>
                        <p className="mt-1 font-mono text-xs text-slate-400">{booking.bookingId}</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {canRefundBookings && canRefund ? (
                        <Button variant="outline" size="sm" className="text-blue-600 hover:bg-blue-50 dark:text-blue-400" onClick={() => setRefundModal(true)}>
                            <ArrowPathIcon className="mr-1.5 h-4 w-4" />Refund
                        </Button>
                    ) : null}
                    {canManageBookings && canCancel ? (
                        <Button variant="outline" size="sm" className="text-rose-600 hover:bg-rose-50 dark:text-rose-400" onClick={() => setCancelModal(true)}>
                            <XCircleIcon className="mr-1.5 h-4 w-4" />Cancel
                        </Button>
                    ) : null}
                </div>
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                {/* Route & Pricing */}
                <div className="space-y-6 lg:col-span-2">
                    {/* Route Card */}
                    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                        <div className="border-b border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-800/50">
                            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Route Details</h2>
                        </div>
                        <div className="space-y-4 p-6">
                            <div className="flex items-start gap-4">
                                <div className="mt-1 flex flex-col items-center gap-0.5">
                                    <div className="h-3 w-3 rounded-full border-2 border-emerald-500 bg-emerald-100" />
                                    <div className="h-10 w-0.5 bg-slate-200 dark:bg-slate-700" />
                                    <div className="h-3 w-3 rounded-full border-2 border-rose-500 bg-rose-100" />
                                </div>
                                <div className="flex-1 space-y-4">
                                    <div>
                                        <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Pickup</p>
                                        <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{booking.pickupAddress}</p>
                                        <p className="mt-0.5 font-mono text-xs text-slate-400">{booking.pickupLatitude?.toFixed(4)}, {booking.pickupLongitude?.toFixed(4)}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Dropoff</p>
                                        <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{booking.dropoffAddress}</p>
                                        <p className="mt-0.5 font-mono text-xs text-slate-400">{booking.dropoffLatitude?.toFixed(4)}, {booking.dropoffLongitude?.toFixed(4)}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Pricing Card */}
                    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                        <div className="border-b border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-800/50">
                            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Pricing & Payment</h2>
                        </div>
                        <div className="grid grid-cols-2 gap-4 p-6 sm:grid-cols-4">
                            <div>
                                <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Seats</p>
                                <p className="mt-1 text-xl font-bold text-slate-900 dark:text-slate-100">{booking.numberOfSeats}</p>
                            </div>
                            <div>
                                <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Price/Seat</p>
                                <p className="mt-1 text-xl font-bold text-slate-900 dark:text-slate-100">{formatCurrency(booking.pricePerSeat, booking.currency)}</p>
                            </div>
                            <div>
                                <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Platform Fee</p>
                                <p className="mt-1 text-xl font-bold text-slate-900 dark:text-slate-100">{formatCurrency(booking.platformFee, booking.currency)}</p>
                            </div>
                            <div>
                                <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Total</p>
                                <p className="mt-1 text-xl font-bold text-emerald-600 dark:text-emerald-400">{formatCurrency(totalAmount, booking.currency)}</p>
                            </div>
                        </div>
                        <div className="border-t border-slate-100 px-6 py-3 dark:border-slate-800">
                            <div className="flex items-center gap-4 text-sm">
                                <span className="text-slate-500 dark:text-slate-400">Payment Method:</span>
                                <span className="font-medium text-slate-700 dark:text-slate-300">{booking.paymentMethod || "—"}</span>
                            </div>
                        </div>
                    </div>

                    {/* Trip Info */}
                    {booking.trip && (
                        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                            <div className="border-b border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-800/50">
                                <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Trip Information</h2>
                            </div>
                            <div className="space-y-3 p-6">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-slate-500 dark:text-slate-400">Trip ID</span>
                                    <button
                                        onClick={() => navigate(`/ride-bookings-trips/trips/${booking.trip!.tripId}`)}
                                        className="font-mono text-sm font-medium text-primary-600 hover:underline"
                                    >
                                        {booking.trip.tripId.slice(0, 8)}...
                                    </button>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-slate-500 dark:text-slate-400">Status</span>
                                    <StatusBadge status={booking.trip.status} />
                                </div>
                                {booking.trip.startedAt && (
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-slate-500 dark:text-slate-400">Started</span>
                                        <span className="text-sm text-slate-700 dark:text-slate-300">{formatDate(booking.trip.startedAt)}</span>
                                    </div>
                                )}
                                {booking.trip.completedAt && (
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-slate-500 dark:text-slate-400">Completed</span>
                                        <span className="text-sm text-slate-700 dark:text-slate-300">{formatDate(booking.trip.completedAt)}</span>
                                    </div>
                                )}
                                {booking.trip.driverRating !== null && (
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-slate-500 dark:text-slate-400">Driver Rating</span>
                                        <div className="flex items-center gap-1">
                                            <StarIcon className="h-4 w-4 text-amber-400" />
                                            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{booking.trip.driverRating}</span>
                                        </div>
                                    </div>
                                )}
                                {booking.trip.passengerRating !== null && (
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-slate-500 dark:text-slate-400">Passenger Rating</span>
                                        <div className="flex items-center gap-1">
                                            <StarIcon className="h-4 w-4 text-amber-400" />
                                            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{booking.trip.passengerRating}</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Sidebar */}
                <div className="space-y-4">
                    {/* Rider Card */}
                    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                        <div className="border-b border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-800/50">
                            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Rider</h2>
                        </div>
                        <div className="space-y-3 p-6">
                            <div className="flex items-center gap-3">
                                <UserCircleIcon className="h-5 w-5 text-slate-400" />
                                <span className="text-sm font-medium text-slate-900 dark:text-slate-100">{booking.rider.firstName} {booking.rider.lastName}</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <EnvelopeIcon className="h-5 w-5 text-slate-400" />
                                <span className="text-sm text-slate-600 dark:text-slate-400">{booking.rider.email}</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <PhoneIcon className="h-5 w-5 text-slate-400" />
                                <span className="text-sm text-slate-600 dark:text-slate-400">{booking.rider.phone}</span>
                            </div>
                            <Button variant="ghost" size="sm" className="mt-2 w-full text-primary-600" onClick={() => navigate(`/riders/${booking.rider.userId}`)}>
                                View Rider Profile →
                            </Button>
                        </div>
                    </div>

                    {/* Ride Offer Card */}
                    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                        <div className="border-b border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-800/50">
                            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Ride Offer</h2>
                        </div>
                        <div className="space-y-3 p-6">
                            <div className="flex items-center gap-3">
                                <TruckIcon className="h-5 w-5 text-slate-400" />
                                <span className="text-sm text-slate-600 dark:text-slate-400">{booking.rideOffer.vehicleType}</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <UserCircleIcon className="h-5 w-5 text-slate-400" />
                                <span className="text-sm font-medium text-slate-900 dark:text-slate-100">{booking.rideOffer.driverName}</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <MapPinIcon className="h-5 w-5 text-emerald-500" />
                                <span className="text-sm text-slate-600 dark:text-slate-400">{booking.rideOffer.origin}</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <MapPinIcon className="h-5 w-5 text-rose-500" />
                                <span className="text-sm text-slate-600 dark:text-slate-400">{booking.rideOffer.destination}</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <CalendarIcon className="h-5 w-5 text-slate-400" />
                                <span className="text-sm text-slate-600 dark:text-slate-400">{formatDate(booking.rideOffer.departureTime)}</span>
                            </div>
                            <Button variant="ghost" size="sm" className="mt-2 w-full text-primary-600" onClick={() => navigate(`/ride-bookings-trips/rides/${booking.rideOffer.rideOfferId}`)}>
                                View Ride Offer →
                            </Button>
                        </div>
                    </div>

                    {/* Timestamps */}
                    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                        <div className="border-b border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-800/50">
                            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Timeline</h2>
                        </div>
                        <div className="space-y-3 p-6">
                            <div className="flex items-center gap-3">
                                <ClockIcon className="h-4 w-4 text-slate-400" />
                                <div>
                                    <p className="text-xs text-slate-500 dark:text-slate-400">Created</p>
                                    <p className="text-sm font-medium text-slate-700 dark:text-slate-300">{formatDate(booking.bookedAt || booking.createdAt || null)}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <ClockIcon className="h-4 w-4 text-slate-400" />
                                <div>
                                    <p className="text-xs text-slate-500 dark:text-slate-400">Last Updated</p>
                                    <p className="text-sm font-medium text-slate-700 dark:text-slate-300">{formatDate(booking.updatedAt)}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Cancel Modal */}
            {cancelModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => { setCancelModal(false); setActionReason(""); }}>
                    <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl dark:bg-slate-900" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Cancel Booking</h3>
                        <p className="mt-2 text-sm text-slate-500">This will cancel the booking and release the seats.</p>
                        <textarea
                            rows={3}
                            placeholder="Cancellation reason..."
                            value={actionReason}
                            onChange={(e) => setActionReason(e.target.value)}
                            className="mt-4 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                        />
                        <div className="mt-4 flex justify-end gap-3">
                            <Button variant="outline" size="sm" onClick={() => { setCancelModal(false); setActionReason(""); }}>Go Back</Button>
                            <Button size="sm" className="bg-rose-600 hover:bg-rose-700" disabled={!actionReason.trim() || cancelMutation.isPending} onClick={() => cancelMutation.mutate(actionReason)}>
                                {cancelMutation.isPending ? "Cancelling..." : "Cancel Booking"}
                            </Button>
                        </div>
                        {cancelMutation.isError && <p className="mt-3 text-sm text-rose-600">{extractApiError(cancelMutation.error)}</p>}
                    </div>
                </div>
            )}

            {/* Refund Modal */}
            {refundModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => { setRefundModal(false); setActionReason(""); setRefundAmount(""); }}>
                    <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl dark:bg-slate-900" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Process Refund</h3>
                        <p className="mt-2 text-sm text-slate-500">Original amount: <span className="font-semibold">{formatCurrency(totalAmount, booking.currency)}</span></p>
                        <div className="mt-4 space-y-3">
                            <div>
                                <label className="text-xs font-medium text-slate-500">Refund Amount (leave empty for full refund)</label>
                                <input type="number" placeholder={totalAmount.toString()} value={refundAmount} onChange={(e) => setRefundAmount(e.target.value)}
                                    className="mt-1 h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                                />
                            </div>
                            <textarea rows={3} placeholder="Reason..." value={actionReason} onChange={(e) => setActionReason(e.target.value)}
                                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                            />
                        </div>
                        <div className="mt-4 flex justify-end gap-3">
                            <Button variant="outline" size="sm" onClick={() => { setRefundModal(false); setActionReason(""); setRefundAmount(""); }}>Go Back</Button>
                            <Button size="sm" className="bg-blue-600 hover:bg-blue-700" disabled={refundMutation.isPending}
                                onClick={() => refundMutation.mutate({ reason: actionReason, amount: refundAmount ? parseFloat(refundAmount) : undefined })}
                            >
                                {refundMutation.isPending ? "Processing..." : "Process Refund"}
                            </Button>
                        </div>
                        {refundMutation.isError && <p className="mt-3 text-sm text-rose-600">{extractApiError(refundMutation.error)}</p>}
                    </div>
                </div>
            )}
        </div>
    );
};
